import { useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { handlePriceWithDate, handlePriceWithRM } from "../../../utils/priceUtils";
import { convertDateTimeToDateString } from "../../../utils/datetimeUtils";
import { Box, Chip, Typography } from "@mui/material";
import { DownloadIcon } from "@phosphor-icons/react";
import { download, generateCsv, mkConfig } from "export-to-csv";
import CommissionProgressBar from "./commissionProgressBar";
import useTableState from "../../../hooks/useTableState";

// All available column definitions
const ALL_COLUMNS = (createStatusCell) => ({
    salesId: { id: 'salesId', header: 'Sales ID', accessorFn: row => row.salesDetailsForCommissionProgress?.salesId, size: 80 },
    depositDate: {
        id: 'depositDate', header: 'Deposit Date',
        accessorFn: row => row.salesDetailsForCommissionProgress?.depositOn ? new Date(row.salesDetailsForCommissionProgress.depositOn).getTime() : null,
        Cell: ({ row }) => convertDateTimeToDateString(row.original.salesDetailsForCommissionProgress?.depositOn),
        size: 110,
    },
    customer: { id: 'customer', header: 'Customer', accessorFn: row => row.salesDetailsForCommissionProgress?.customerName ?? '', size: 140 },
    productCategory: { id: 'productCategory', header: 'Category', accessorFn: row => row.salesDetailsForCommissionProgress?.productCategory ?? '', size: 100 },
    productEnergy: { id: 'productEnergy', header: 'Energy', accessorFn: row => `${row.salesDetailsForCommissionProgress?.energy ?? ""} kWp`.trim(), size: 80 },
    oriPrice: {
        id: 'oriPrice', header: 'Ori Price', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.originalPrice,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.originalPrice; return v != null ? handlePriceWithRM(v) : ""; },
    },
    asternDiscount: {
        id: 'asternDiscount', header: 'Astern Disc.', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.discountByAstern,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.discountByAstern; return v != null ? handlePriceWithRM(v) : ""; },
    },
    agentDiscount: {
        id: 'agentDiscount', header: 'Agent Disc.', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.discountByAgent,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.discountByAgent; return v != null ? handlePriceWithRM(v) : ""; },
    },
    finalPrice: {
        id: 'finalPrice', header: 'Final Price', size: 100,
        accessorFn: row => row.salesDetailsForCommissionProgress?.finalPrice,
        Cell: ({ row }) => { const v = row.original.salesDetailsForCommissionProgress?.finalPrice; return v != null ? handlePriceWithRM(v) : ""; },
    },
    agent: { id: 'agent', header: 'Agent', accessorFn: row => row.salesDetailsForCommissionProgress?.agentName ?? '', size: 120 },
    coAgent: { id: 'coAgent', header: 'Co-Agent (Offline)', accessorFn: row => row.salesDetailsForCommissionProgress?.coAgentName ?? '', size: 120 },
    commissionType: { id: 'commissionType', header: 'Comm Type', accessorFn: row => row.commissionProgressDto?.commPayType ?? '', size: 90 },
    paymentTo: { id: 'paymentTo', header: 'Payment To', accessorFn: row => row.commissionProgressDto?.paymentToName ?? '', size: 120 },
    commission: {
        id: 'commission', header: 'Commission', size: 100,
        accessorFn: row => row.commissionProgressDto?.commPay,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.commPay; return v != null ? handlePriceWithRM(v) : ""; },
    },
    firstPayment: {
        id: 'firstPayment', header: '1st Pay', size: 100,
        accessorFn: row => row.commissionProgressDto?.firstPayment,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.firstPayment; return v != null ? handlePriceWithRM(v) : ""; },
    },
    firstPaymentDate: {
        id: 'firstPaymentDate', header: '1st Pay Date', size: 100,
        accessorFn: row => row.commissionProgressDto?.firstPaymentOn,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.firstPaymentOn; return v ? convertDateTimeToDateString(v) : ""; },
    },
    secondPayment: {
        id: 'secondPayment', header: '2nd Pay', size: 100,
        accessorFn: row => row.commissionProgressDto?.secondPayment,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.secondPayment; return v != null ? handlePriceWithRM(v) : ""; },
    },
    secondPaymentDate: {
        id: 'secondPaymentDate', header: '2nd Pay Date', size: 100,
        accessorFn: row => row.commissionProgressDto?.secondPaymentOn,
        Cell: ({ row }) => { const v = row.original.commissionProgressDto?.secondPaymentOn; return v ? convertDateTimeToDateString(v) : ""; },
    },
    progress: {
        id: 'progress', header: 'Progress', size: 110,
        accessorFn: row => {
            const cp = row.commissionProgressDto;
            if (!cp || !cp.commPay) return 0;
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            return paid / cp.commPay * 100;
        },
        Cell: ({ row }) => {
            const cp = row.original?.commissionProgressDto;
            const allRepay = (cp?.repayment || 0) + (cp?.repayment2 || 0) + (cp?.repayment3 || 0) + (cp?.repayment4 || 0) + (cp?.repayment5 || 0);
            const paid = (cp?.firstPayment || 0) + (cp?.secondPayment || 0) + allRepay;
            return <CommissionProgressBar paid={paid} total={cp?.commPay || 0} />;
        },
    },
    status: {
        id: 'status', header: 'Status', size: 100,
        filterVariant: 'select',
        filterSelectOptions: ['Paid', 'Pending', 'Overpaid'],
        accessorFn: row => {
            const cp = row.commissionProgressDto;
            if (!cp || !cp.commPay) return 'Pending';
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            const pct = paid / cp.commPay * 100;
            if (pct > 100.01) return 'Overpaid';
            if (pct >= 99.99) return 'Paid';
            return 'Pending';
        },
        Cell: ({ row }) => {
            const cp = row.original?.commissionProgressDto;
            if (!cp || !cp.commPay) return <Chip color="default" label="Pending" size="small" />;
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            const pct = paid / cp.commPay * 100;
            if (pct > 100.01) return <Chip color="error" label="Overpaid" size="small" />;
            if (pct >= 99.99) return <Chip color="success" label="Paid" size="small" />;
            return <Chip sx={{ backgroundColor: '#FEF3C7', color: '#92400E', fontWeight: 600 }} label="Pending" size="small" />;
        },
    },
    paymentStatus: {
        id: 'paymentStatus', header: 'Pay Status', size: 100,
        accessorFn: row => row.commissionProgressDto?.paymentStatus,
        Cell: createStatusCell(row => row?.commissionProgressDto?.paymentStatus),
    },
    repayment: {
        id: 'repayment', header: '3rd Pay', size: 100,
        accessorFn: row => row.commissionProgressDto?.repayment,
        Cell: ({ row }) => {
            const cp = row.original.commissionProgressDto;
            return (cp?.repayment != null) ? handlePriceWithDate(cp.repayment, cp.repaymentOn) : "";
        },
    },
    repaymentStatus: {
        id: 'repaymentStatus', header: '3rd Pay Status', size: 100,
        accessorFn: row => row.commissionProgressDto?.repaymentStatus,
        Cell: createStatusCell(row => row?.commissionProgressDto?.repaymentStatus),
    },
    updatedBy: { id: 'updatedBy', header: 'Updated By', accessorKey: 'updatedBy', size: 100 },
    updatedOn: {
        id: 'updatedOn', header: 'Date', size: 100,
        accessorFn: (row) => { try { return new Date(row.updatedOn); } catch { return null; } },
        Cell: ({ row }) => row.original ? convertDateTimeToDateString(row.original.updatedOn) : "",
    },
    actions: null, // placeholder — built dynamically with onRowClick
});

// Default column order (all columns)
const DEFAULT_VISIBLE = [
    'salesId', 'depositDate', 'customer', 'productCategory', 'productEnergy',
    'oriPrice', 'asternDiscount', 'agentDiscount', 'finalPrice',
    'agent', 'coAgent', 'commissionType', 'paymentTo', 'commission',
    'firstPayment', 'firstPaymentDate', 'secondPayment', 'secondPaymentDate',
    'progress', 'status', 'repayment', 'updatedBy', 'updatedOn',
];

export default function GeneralCommissionProgressList({
    fetchData,
    onRowClick,
    visibleColumns,
    detailRoute,
}) {
    const navigate = useNavigate();
    const ts = useTableState('', { defaultSorting: [{ id: 'depositDate', desc: true }] });
    const statusMap = {
        'In Progress': { label: 'In Progress', color: 'info' },
        'Complete': { label: 'Complete', color: 'success' },
        'Completed': { label: 'Completed', color: 'success' },
        'Settled': { label: 'Settled', color: 'success' },
        'Underpaid': { label: 'Underpaid', color: 'warning' },
        'Overpaid': { label: 'Overpaid', color: 'error' },
    };

    const createStatusCell = (accessor) => ({ row }) => {
        const status = accessor(row.original);
        if (!status) return <span style={{ color: '#94A3B8', fontSize: '0.75rem' }}>{'\u2014'}</span>;
        const { label, color } = statusMap[status] ?? { label: status, color: 'default' };
        return <Chip color={color} label={label} size="small" />;
    };

    const allCols = ALL_COLUMNS(createStatusCell);

    // Build actions column dynamically since it needs onRowClick
    const actionsCol = onRowClick ? {
        id: 'actions', header: '', size: 60, enableSorting: false, enableColumnFilter: false,
        Cell: ({ row }) => (
            <span
                onClick={(e) => { e.stopPropagation(); navigate(`/accounting/commission/${row.original.commProgressId}`); }}
                style={{ color: '#1A8CFF', cursor: 'pointer', fontWeight: 500, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}
            >
                Edit
            </span>
        ),
    } : null;

    const colKeys = visibleColumns || DEFAULT_VISIBLE;
    const columns = useMemo(() => {
        const cols = colKeys.map(key => key === 'actions' ? actionsCol : allCols[key]).filter(Boolean);
        return cols;
    }, [colKeys, actionsCol]);

    // Export helpers
    const csvConfig = mkConfig({ filename: 'Commission_Data_Export', fieldSeparator: ',', decimalSeparator: '.', useKeysAsHeaders: true });

    const EXPORT_FIELD_MAP = {
        salesId: (r) => ({ 'Sales ID': r.salesDetailsForCommissionProgress?.salesId ?? '' }),
        depositDate: (r) => ({ 'Deposit Date': convertDateTimeToDateString(r.salesDetailsForCommissionProgress?.depositOn) }),
        customer: (r) => ({ 'Customer': r.salesDetailsForCommissionProgress?.customerName ?? '' }),
        productCategory: (r) => ({ 'Category': r.salesDetailsForCommissionProgress?.productCategory ?? '' }),
        productEnergy: (r) => ({ 'Energy (kWp)': r.salesDetailsForCommissionProgress?.energy ?? '' }),
        oriPrice: (r) => ({ 'Ori Price': r.salesDetailsForCommissionProgress?.originalPrice ?? '' }),
        asternDiscount: (r) => ({ 'Astern Disc.': r.salesDetailsForCommissionProgress?.discountByAstern ?? '' }),
        agentDiscount: (r) => ({ 'Agent Disc.': r.salesDetailsForCommissionProgress?.discountByAgent ?? '' }),
        finalPrice: (r) => ({ 'Final Price': r.salesDetailsForCommissionProgress?.finalPrice ?? '' }),
        agent: (r) => ({ 'Agent': r.salesDetailsForCommissionProgress?.agentName ?? '' }),
        coAgent: (r) => ({ 'CoAgent': r.salesDetailsForCommissionProgress?.coAgentName ?? '' }),
        commissionType: (r) => ({ 'Comm Type': r.commissionProgressDto?.commPayType ?? '' }),
        paymentTo: (r) => ({ 'Payment To': r.commissionProgressDto?.paymentToName ?? '' }),
        commission: (r) => ({ 'Commission': r.commissionProgressDto?.commPay ?? '' }),
        firstPayment: (r) => ({ '1st Pay': r.commissionProgressDto?.firstPayment ?? '' }),
        firstPaymentDate: (r) => ({ '1st Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.firstPaymentOn) }),
        secondPayment: (r) => ({ '2nd Pay': r.commissionProgressDto?.secondPayment ?? '' }),
        secondPaymentDate: (r) => ({ '2nd Pay Date': convertDateTimeToDateString(r.commissionProgressDto?.secondPaymentOn) }),
        progress: (r) => {
            const cp = r.commissionProgressDto;
            const paid = (cp?.firstPayment || 0) + (cp?.secondPayment || 0);
            const pct = cp?.commPay ? Math.round(paid / cp.commPay * 100) : 0;
            return { 'Progress %': pct };
        },
        status: (r) => {
            const cp = r.commissionProgressDto;
            if (!cp || !cp.commPay) return { 'Status': 'Pending' };
            const allRepay = (cp.repayment || 0) + (cp.repayment2 || 0) + (cp.repayment3 || 0) + (cp.repayment4 || 0) + (cp.repayment5 || 0);
            const paid = (cp.firstPayment || 0) + (cp.secondPayment || 0) + allRepay;
            const pct = paid / cp.commPay * 100;
            const label = pct > 100.01 ? 'Overpaid' : pct >= 99.99 ? 'Paid' : 'Pending';
            return { 'Status': label };
        },
        paymentStatus: (r) => ({ 'Pay Status': r.commissionProgressDto?.paymentStatus ?? '' }),
        repayment: (r) => ({ '3rd Pay': r.commissionProgressDto?.repayment ?? '' }),
        repaymentStatus: (r) => ({ '3rd Pay Status': r.commissionProgressDto?.repaymentStatus ?? '' }),
        updatedBy: (r) => ({ 'Updated By': r.updatedBy ?? '' }),
        updatedOn: (r) => ({ 'Date': convertDateTimeToDateString(r.updatedOn) }),
    };

    const exportKeys = colKeys.filter(k => k !== 'actions' && EXPORT_FIELD_MAP[k]);

    const mapCommForExport = (data) => data.map(r => {
        const row = {};
        exportKeys.forEach(k => Object.assign(row, EXPORT_FIELD_MAP[k](r)));
        return row;
    });

    const handleExportAll = () => {
        const csv = generateCsv(csvConfig)(mapCommForExport(fetchData || []));
        download(csvConfig)(csv);
    };

    const handleExportSelected = (rows) => {
        const csv = generateCsv(csvConfig)(mapCommForExport(rows.map(r => r.original)));
        download(csvConfig)(csv);
    };

    const table = useMaterialReactTable({
        columns,
        data: fetchData || [],
        enableColumnActions: false,
        enableColumnDragging: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableStickyHeader: true,
        enableRowSelection: true,
        // Mobile: let vertical scroll flow to the page (no inner table scroll box).
        // Wide tables still scroll horizontally; sticky header just won't trap scroll.
        muiTableContainerProps: { sx: { maxHeight: 'unset' } },
        initialState: { density: 'compact', showGlobalFilter: true },
        state: { globalFilter: ts.globalFilter, sorting: ts.sorting, pagination: ts.pagination, columnFilters: ts.columnFilters },
        onGlobalFilterChange: ts.setGlobalFilter,
        onSortingChange: ts.setSorting,
        onPaginationChange: ts.setPagination,
        onColumnFiltersChange: ts.setColumnFilters,
        muiTablePaperProps: { elevation: 0, sx: { border: 'none', backgroundColor: 'transparent', borderRadius: '14px' } },
        muiTableHeadCellProps: { sx: { backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0', textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.5px', color: '#64748B', fontFamily: "'Poppins', sans-serif", padding: '8px 10px' } },
        muiTableBodyCellProps: { sx: { fontSize: '0.8rem', color: '#374151', borderBottom: '1px solid #F1F5F9', fontFamily: "'Poppins', sans-serif", padding: '6px 10px' } },
        muiTableBodyRowProps: ({ row }) => ({
            onClick: (e) => {
                if (e.target.closest('input[type="checkbox"]') || (e.target.tagName === 'SPAN' && e.target.style.cursor === 'pointer')) return;
                const clickable = onRowClick || detailRoute;
                if (!clickable) return;
                const url = detailRoute
                    ? `${detailRoute}/${row.original.commProgressId}`
                    : `/accounting/commission/${row.original.commProgressId}`;
                if (e.ctrlKey || e.metaKey) {
                    window.open(url, '_blank');
                } else {
                    navigate(url);
                }
            },
            sx: { cursor: (onRowClick || detailRoute) ? 'pointer' : 'default', '&:hover': { backgroundColor: (onRowClick || detailRoute) ? '#F0F7FF' : '#F8FAFC', transition: 'background-color 0.12s' } },
        }),
        muiTopToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', minHeight: '48px' } },
        muiBottomToolbarProps: { sx: { backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0' } },
        muiSearchTextFieldProps: { placeholder: 'Search...', sx: { '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#F8FAFC', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" } } },
        muiPaginationProps: { shape: 'rounded', showRowsPerPage: true, rowsPerPageOptions: [10, 25, 50, 100] },
        renderTopToolbarCustomActions: ({ table }) => {
            const selectedRows = table.getSelectedRowModel().rows;
            const hasSelection = selectedRows.length > 0;
            return (
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={handleExportAll} style={{ border: '1px solid #E2E8F0', background: 'white', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#374151', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <DownloadIcon size={14} /> Export All
                    </button>
                    {hasSelection && (
                        <button onClick={() => handleExportSelected(selectedRows)} style={{ border: '1px solid #1A8CFF', background: '#EFF6FF', borderRadius: '7px', padding: '6px 14px', fontSize: '0.775rem', fontWeight: 500, color: '#1A8CFF', cursor: 'pointer', fontFamily: "'Poppins', sans-serif", display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <DownloadIcon size={14} /> Export Selected ({selectedRows.length})
                        </button>
                    )}
                </Box>
            );
        },
        renderBottomToolbarCustomActions: ({ table }) => {
            const filteredRows = table.getFilteredRowModel().rows;
            const caseCount = filteredRows.length;
            const totalCommission = filteredRows.reduce((sum, r) => sum + (r.original?.commissionProgressDto?.commPay || 0), 0);
            return (
                <Typography component="div" sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#475569', fontFamily: "'Poppins', sans-serif", pl: '8px' }}>
                    <span>Cases: {caseCount}</span>
                    <span>{'·'}</span>
                    <span>Total Commission: {handlePriceWithRM(totalCommission)}</span>
                </Typography>
            );
        },
    });

    return (
        <div style={{ backgroundColor: '#fff', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <MaterialReactTable table={table} />
        </div>
    );
}
